import { Box, Stack, Typography } from '@mui/material';
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  nextMonday,
  parseISO,
} from 'date-fns';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { EnumCurrency, mapCurrency } from 'tdc-web-backend/enums/enums';
import { useTranslation } from 'react-i18next';
import ControlledDateInput from '../../../../../../components/custom-inputs/ControlledDateInput';
import CustomInput from '../../../../../../components/custom-input/CustomInput';
import ConfirmModal from '../../../../../../components/confirm-modal/ConfirmModal';
import ControlledContractMilestoneNumberInput from '../../../../../../components/custom-inputs/ControlledContractMilestoneNumberInput';
import BaseInputLabel from '../../../../../../components/custom-inputs/BaseInputLabel';
import { WrapText } from '@mui/icons-material';

export type AddMilestoneModalProps = {
  open: boolean;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onConfirm: (milestones: any[]) => void;
  minStartDate?: Date | null;
  currency: EnumCurrency;
  currentProjectBudget: number;
};

type FormValues = {
  count: number;
  name: string;
  budget: number;
  budgetType: 'copy' | 'split';
  description: string;
  startDate: string; // ISO string
  endDate: string; // ISO string
  interval: 'day' | 'week' | 'week_dtd' | 'month' | 'month_dtd';
};

const AddMilestoneModal = ({
  open,
  onClose,
  onConfirm,
  minStartDate,
  currency,
  currentProjectBudget,
}: AddMilestoneModalProps) => {
  const { t, i18n } = useTranslation();
  const methods = useForm<FormValues>({
    defaultValues: {
      count: 1,
      name: '',
      budget: 0,
      budgetType: 'copy',
      description: '',
      startDate: '',
      endDate: '',
      interval: 'month',
    },
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitted },
  } = methods;
  const { count = 1, startDate, endDate, budget, budgetType } = useWatch({ control });

  useEffect(() => {
    if (open) {
      reset({
        count: 1,
        name: '',
        budget: 0,
        budgetType: 'copy',
        description: '',
        startDate: '',
        endDate: '',
        interval: 'month',
      });
    }
  }, [open, reset]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const startDateParsed = parseISO(startDate);
    const endDateParsed = parseISO(endDate);

    if (Number.isNaN(startDateParsed.getTime()) || Number.isNaN(endDateParsed.getTime())) return;

    if (endDateParsed < startDateParsed) {
      setValue('endDate', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [startDate, endDate, setValue]);

  const handleConfirm = () => {
    handleSubmit((data) => {
      const milestones = [];
      const initialStart = parseISO(data.startDate);
      let durationDays = 0;

      // Only calculate duration for single milestone or specific interval types if needed
      if (data.count === 1 && data.endDate) {
        const initialEnd = parseISO(data.endDate);
        durationDays = differenceInCalendarDays(initialEnd, initialStart);
      }

      // Validate duration is positive for single milestone
      if (data.count === 1 && durationDays < 0) return;

      let currentStart = initialStart;

      for (let i = 0; i < data.count; i += 1) {
        // Calculate Budget
        let { budget: milestoneBudget } = data;
        if (data.count > 1 && data.budgetType === 'split') {
          milestoneBudget = data.budget / data.count;
        }

        // Calculate Name
        let name = data.name;
        if (data.count > 1) {
          const intervalLabelMap = {
            day: 'common.day',
            week: 'common.week',
            week_dtd: 'common.week',
            month: 'common.month',
            month_dtd: 'common.month',
          };
          // 1-based index for display
          name = `${data.name} (${t(intervalLabelMap[data.interval])} ${i + 1})`;
        }

        // Calculate Dates
        let start = currentStart;
        let end;

        if (data.count === 1) {
          end = addDays(start, durationDays);
        } else {
          // Multi-milestone logic
          switch (data.interval) {
            case 'week': // Calendar Week (Mon-Sun)
              // First milestone starts on StartDate, ends on next Sunday
              // Subsequent (handled below) start on Monday
              end = endOfWeek(start, { weekStartsOn: 1 });
              break;
            case 'week_dtd': // Date to Date (7 days)
              end = addWeeks(start, 1);
              end = addDays(end, -1);
              break;
            case 'month': // Calendar Month
              end = endOfMonth(start);
              break;
            case 'month_dtd': // Date to Date (1 month)
              end = addMonths(start, 1);
              end = addDays(end, -1);
              break;
            case 'day':
            default:
              end = start; // 1 Day duration
              break;
          }
        }

        if (i > 0) {
          // Logic for subsequent milestones
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const { endObj: prevEnd } = milestones[i - 1]!; // Previous end

          // Determine next start based on logic
          let nextStartCalculated;

          switch (data.interval) {
            case 'week':
              // Calendar week: Next start is next Monday after prevEnd
              nextStartCalculated = nextMonday(prevEnd);
              break;
            case 'week_dtd':
              // Day to day: Next start is prevStart + 1 week
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              nextStartCalculated = addWeeks(milestones[i - 1]!.startObj, 1);
              break;
            case 'month':
              // Calendar month: Next start is 1st of next month after prevEnd
              nextStartCalculated = addDays(prevEnd, 1);
              break;
            case 'month_dtd':
              // Date to Date: Next start is prevStart + 1 month
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              nextStartCalculated = addMonths(milestones[i - 1]!.startObj, 1);
              break;
            case 'day':
              nextStartCalculated = addDays(prevEnd, 1);
              break;
            default:
              nextStartCalculated = addDays(prevEnd, 1);
          }

          // Safety check: Ensure strict non-overlap if calculated start is too early (shouldn't happen with above logic but good for safety)
          if (nextStartCalculated <= prevEnd) {
            nextStartCalculated = addDays(prevEnd, 1);
          }

          start = nextStartCalculated;
          currentStart = start;

          // Re-calculate End for the new Start
          switch (data.interval) {
            case 'week':
              end = endOfWeek(start, { weekStartsOn: 1 });
              break;
            case 'week_dtd':
              end = addWeeks(start, 1);
              end = addDays(end, -1);
              break;
            case 'month':
              end = endOfMonth(start);
              break;
            case 'month_dtd':
              end = addMonths(start, 1);
              end = addDays(end, -1);
              break;
            case 'day':
            default:
              end = start;
              break;
          }
        }

        milestones.push({
          name,
          budget: milestoneBudget,
          summary: data.description,
          start: format(start, "yyyy-MM-dd'T'HH:mm:ss"),
          end: format(end, "yyyy-MM-dd'T'HH:mm:ss"),
          startObj: start, // Temp for calculation
          endObj: end, // Temp for calculation
          duration: null,
          recurring: false,
        });
      }

      // cleanup temp objects
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const finalMilestones = milestones.map(({ startObj, endObj, ...rest }) => rest);
      onConfirm(finalMilestones);
      onClose();
    })();
  };

  const totalAddedBudget = budgetType === 'copy' ? (budget || 0) * count : budget || 0;
  const perMilestoneBudget = budgetType === 'split' ? (budget || 0) / count : budget || 0;
  const newProjectTotalBudget = (currentProjectBudget || 0) + totalAddedBudget;

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'standard',
    maximumFractionDigits: 2,
  });

  return (
    <ConfirmModal
      isModalOpen={open}
      setIsModalOpen={() => null}
      title={t('contracts.createNewMilestoneMilestones')}
      confirmButtonText={t('common.confirm')}
      onConfirm={handleConfirm}
      showCancelButton
      onCancel={onClose}
      automaticallyCloseModalOnButtonClick={false}
      width="800px" // Wider modal for grid
      maxWidth="lg"
      message={
        <FormProvider {...methods}>
          <Stack spacing={4} pt={1}>
            {/* ROW 1: Name and Count */}
            <Stack direction="row" justifyContent="space-between">
              <Box sx={{ width: '340px' }}>
                <CustomInput
                  sx={{ width: '100%' }}
                  name="name"
                  label={t('projects.milestoneName')}
                  required
                  fullWidth
                  input={<CustomInput.Text />}
                />
              </Box>
              <Stack spacing={2} sx={{ width: '152px' }}>
                <BaseInputLabel
                  sx={{
                    textWrap: 'wrap',
                  }}
                  required
                  text={t('contracts.currentProjectBudget')}
                />
                <Typography variant="body2" sx={{ mt: '21px !important' }}>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency,
                    notation: 'standard',
                    maximumFractionDigits: 2,
                  }).format(currentProjectBudget)}
                </Typography>
              </Stack>
            </Stack>

            {/* ROW 2: Dates */}
            <Stack direction="row" spacing={3}>
              <Stack spacing={2} sx={{ width: '156px' }}>
                <CustomInput
                  sx={{ width: '100%', height: '20px' }}
                  name="count"
                  label={t('contracts.numberOfMilestones')}
                  required
                  fullWidth
                  input={<CustomInput.Autocomplete />}
                  choices={[
                    { value: 1, label: '1' },
                    { value: 2, label: '2' },
                    { value: 3, label: '3' },
                    { value: 4, label: '4' },
                    { value: 5, label: '5' },
                    { value: 6, label: '6' },
                    { value: 7, label: '7' },
                    { value: 8, label: '8' },
                    { value: 9, label: '9' },
                    { value: 10, label: '10' },
                    { value: 11, label: '11' },
                    { value: 12, label: '12' },
                  ]}
                  valueKey="value"
                  labelKey="label"
                  keepOnlyValueKeys
                  disableClearable
                />
              </Stack>

              <Stack spacing={1} sx={{ width: '160px' }}>
                <BaseInputLabel required text={t('common.startDate')} />
                <ControlledDateInput
                  name="startDate"
                  required={t('errors.startDateRequired')}
                  showErrorAlways={isSubmitted}
                  popperSx={{ zIndex: 99999999 }}
                  sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }}
                />
              </Stack>
              <Stack spacing={0} sx={{ width: '160px' }}>
                <Box sx={{ display: count > 1 ? 'block' : 'none' }}>
                  <CustomInput
                    disableClearable
                    sx={{
                      width: '100%',
                      fontSize: '4px',
                      height: '43px',
                      '& .MuiAutocomplete-popper': {
                        width: '230px !important',
                        position: 'absolute !important',
                        left: '35px !important',
                      },
                    }}
                    name="interval"
                    label="Interval"
                    fullWidth
                    input={<CustomInput.Autocomplete />}
                    choices={[
                      { value: 'day', label: t('common.day') },
                      { value: 'week', label: t('common.week') },
                      { value: 'week_dtd', label: t('common.weekDtd') },
                      { value: 'month', label: t('common.month') },
                      { value: 'month_dtd', label: t('common.monthDtd') },
                    ]}
                    valueKey="value"
                    labelKey="label"
                    keepOnlyValueKeys
                  />
                </Box>
                <Box sx={{ display: count === 1 ? 'block' : 'none' }}>
                  <Stack spacing={1}>
                    <BaseInputLabel required={count === 1} text={t('common.endDate')} />
                    <ControlledDateInput
                      name="endDate"
                      minDate={startDate ? parseISO(startDate) : minStartDate || undefined}
                      required={count === 1 ? t('errors.endDateRequired') : false}
                      showErrorAlways={isSubmitted}
                      popperSx={{ zIndex: 99999999 }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '12px' } }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Stack>

            {/* ROW 3: Budgets */}
            <Stack direction="row" sx={{ pt: 2 }} spacing={3} justifyContent="space-between">
              <Stack spacing={3} direction="row">
                <Stack spacing={1} sx={{ width: '156px' }}>
                  <BaseInputLabel text={t('common.budget')} />
                  <ControlledContractMilestoneNumberInput
                    initialValue={budget ?? 0}
                    name="budget"
                    adornmentText={mapCurrency.get(currency)}
                  />
                </Stack>
                <Stack spacing={2} sx={{ width: '160px' }}>
                  <CustomInput
                    disabled={count === 1}
                    disableClearable
                    sx={{ width: '100%' }}
                    name="budgetType"
                    label={t('contracts.budgetDistribution')}
                    fullWidth
                    input={<CustomInput.Autocomplete />}
                    choices={[
                      { value: 'copy', label: t('common.copy') },
                      { value: 'split', label: t('common.split') },
                    ]}
                    valueKey="value"
                    labelKey="label"
                    keepOnlyValueKeys
                  />
                </Stack>

                <Stack spacing={2} sx={{ width: '160px' }}>
                  <BaseInputLabel required text={t('contracts.perMilestone')} />
                  <Typography variant="body2" sx={{ mt: '21px !important' }}>
                    {currencyFormatter.format(perMilestoneBudget)}
                  </Typography>
                </Stack>
              </Stack>

              <Stack spacing={2} sx={{ width: '152px' }}>
                <BaseInputLabel required text={t('contracts.newProjectBudget')} />
                <Typography variant="body2" sx={{ mt: '21px !important' }}>
                  {currencyFormatter.format(newProjectTotalBudget)}
                </Typography>
              </Stack>
            </Stack>

            {/* ROW 4: Summary */}
            <CustomInput
              name="description"
              label={t('projects.milestoneDescription')}
              required
              fullWidth
              multiline
              rows={4}
              input={<CustomInput.Text />}
            />
          </Stack>
        </FormProvider>
      }
    />
  );
};

export default AddMilestoneModal;
